/**
 * Task Form Component
 * Form for creating and editing tasks (public.tasks).
 *
 * 2026-10-16: the form had never worked. Its dropdowns used Select's `options` prop, which
 * Select ignored (empty Status => submit blocked); it wrote `category` / `estimated_hours`,
 * columns that do not exist (PGRST204); and it sent priority as "low"/"medium" strings to an
 * INTEGER 1-5 column. Now: real options, only real columns (see toTaskWrite), numeric
 * priority, server-side search for organization / contact / event, and a visible error when
 * the server refuses a save.
 */

import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createTask,
  updateTask,
  toTaskWrite,
  listTaskAssignees,
} from "../../services/tasks.service";
import {
  searchOrganizationOptions,
  organizationLabel,
  searchContactOptions,
  contactLabel,
} from "../../services/pickerOptions.service";
import { QUERY_KEYS, TASK_STATUSES } from "../../utils/constants";
import {
  TaskStatus,
  TaskPriority,
  TASK_PRIORITY_LABELS,
  toTaskPriority,
} from "../../types/task.types";
import type { Task } from "../../types/task.types";
import type { UUID } from "../../types/common.types";
import { useAuth } from "../../contexts/AuthContext";
import { CONTACT_PICKER_MIN_CHARS } from "../../utils/contactPickerStatus";
import Button from "../common/Button";
import Input from "../common/Input";
import Select from "../common/Select";
import SearchableSelect from "../common/SearchableSelect";
import EventPicker from "../events/EventPicker";
import Textarea from "../common/Textarea";

interface TaskFormProps {
  task?: Task;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const STATUS_OPTIONS = TASK_STATUSES.map((s) => ({
  value: s.value,
  label: s.label,
}));

// Most urgent first, the way people scan a priority list.
const PRIORITY_OPTIONS = (
  [
    TaskPriority.URGENT,
    TaskPriority.HIGH,
    TaskPriority.MEDIUM,
    TaskPriority.LOW,
    TaskPriority.LOWEST,
  ] as const
).map((p) => ({ value: String(p), label: TASK_PRIORITY_LABELS[p] }));

const describeSaveError = (err: unknown): string => {
  const e = err as { code?: string; message?: string };
  if (e?.code === "42501") {
    return "You do not have permission to save this task.";
  }
  return e?.message || "The task could not be saved. Please try again.";
};

const TaskForm: React.FC<TaskFormProps> = ({ task, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = !!task;

  const [formData, setFormData] = useState({
    title: task?.title || "",
    description: task?.description || "",
    status: (task?.status as string) || TaskStatus.TODO,
    priority: String(toTaskPriority(task?.priority ?? TaskPriority.MEDIUM)),
    due_date: task?.due_date ? task.due_date.split("T")[0] : "",
    assigned_to: task?.assigned_to || "",
    organization_id: task?.organization_id || "",
    contact_id: task?.contact_id || "",
    event_id: task?.event_id || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Who can be assigned: list_task_assignees() (migration 897). A direct profiles SELECT showed
  // managers and staff only themselves (profiles RLS).
  const assignees = useQuery({
    queryKey: ["task-assignees"],
    queryFn: listTaskAssignees,
    staleTime: 5 * 60_000,
  });
  const profiles = assignees.data ?? [];

  const assigneeOptions = [
    { value: "", label: "Unassigned" },
    // Keep a current assignee who is no longer active (or not visible) selectable.
    ...(formData.assigned_to &&
    !profiles.some((p) => p.id === formData.assigned_to)
      ? [{ value: formData.assigned_to, label: "(current assignee)" }]
      : []),
    ...profiles.map((profile) => ({
      value: profile.id,
      label: profile.name,
    })),
  ];

  const createMutation = useMutation({
    mutationFn: createTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TASKS] });
      onSuccess?.();
    },
    onError: (err) => setSubmitError(describeSaveError(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: UUID;
      updates: Parameters<typeof updateTask>[1];
    }) => updateTask(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TASKS] });
      onSuccess?.();
    },
    onError: (err) => setSubmitError(describeSaveError(err)),
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }
    if (!formData.status) {
      newErrors.status = "Status is required";
    }
    if (!formData.priority) {
      newErrors.priority = "Priority is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validate()) {
      return;
    }

    let payload;
    try {
      payload = toTaskWrite(formData);
    } catch (err) {
      setSubmitError(describeSaveError(err));
      return;
    }

    if (isEditing && task) {
      updateMutation.mutate({ id: task.id, updates: payload });
    } else {
      if (!user?.id) {
        setSubmitError("Your session has expired. Please sign in again.");
        return;
      }
      createMutation.mutate({ ...payload, created_by: user.id });
    }
  };

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="task-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Input
            label="Title"
            value={formData.title}
            onChange={(e) => handleChange("title", e.target.value)}
            error={errors.title}
            required
            data-testid="task-title"
          />
        </div>

        <Select
          label="Status"
          value={formData.status}
          onChange={(e) => handleChange("status", e.target.value)}
          options={STATUS_OPTIONS}
          error={errors.status}
          required
          data-testid="task-status"
        />

        <Select
          label="Priority"
          value={formData.priority}
          onChange={(e) => handleChange("priority", e.target.value)}
          options={PRIORITY_OPTIONS}
          error={errors.priority}
          required
          data-testid="task-priority"
        />

        <Input
          label="Due Date"
          type="date"
          value={formData.due_date}
          onChange={(e) => handleChange("due_date", e.target.value)}
          data-testid="task-due-date"
        />

        <Select
          label="Assigned To"
          value={formData.assigned_to}
          onChange={(e) => handleChange("assigned_to", e.target.value)}
          options={assigneeOptions}
          error={
            assignees.isError
              ? "Could not load the people you can assign to."
              : undefined
          }
          data-testid="task-assigned-to"
        />

        <SearchableSelect
          label="Organization"
          value={formData.organization_id}
          onChange={(v) => handleChange("organization_id", v)}
          search={searchOrganizationOptions}
          resolveLabel={organizationLabel}
          queryKeyBase="task-form-organization"
          emptyLabel="None"
          placeholder="Search organizations..."
          testId="task-organization"
        />

        <SearchableSelect
          label="Related Contact"
          value={formData.contact_id}
          onChange={(v) => handleChange("contact_id", v)}
          search={searchContactOptions}
          resolveLabel={contactLabel}
          queryKeyBase="task-form-contact"
          emptyLabel="None"
          minChars={CONTACT_PICKER_MIN_CHARS}
          placeholder="Search by name, email, phone or member #..."
          testId="task-contact"
        />

        {/* The shared, externally audited EventPicker (fix/event-pickers-truncated, GO 8129a1d0):
            keyset "Load more", date and event-ID search, Enter never submits this form. */}
        <EventPicker
          label="Related Event"
          value={formData.event_id}
          onChange={(id) => handleChange("event_id", id)}
          noneLabel="None — leave empty if the task is not about an event."
          inputId="task-event"
          testId="task-event-picker"
        />
      </div>

      <Textarea
        label="Description"
        value={formData.description}
        onChange={(e) => handleChange("description", e.target.value)}
        rows={4}
        data-testid="task-description"
      />

      {submitError && (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-sm"
          data-testid="task-submit-error"
        >
          {submitError}
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isLoading} data-testid="task-submit">
          {isLoading ? "Saving..." : isEditing ? "Update Task" : "Create Task"}
        </Button>
      </div>
    </form>
  );
};

export default TaskForm;
